/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { ComponentProps, memo } from 'react';

import { SvgIconComponent } from '@mui/icons-material';
import { Tooltip } from '@mui/material';

import { useContextWarningsStyles } from './ContextWarnings.styles';

type JustifyIconsVariants = 'center' | 'start' | 'end';

type ContextWarningsProps = {
  variant: 'textField' | 'checkboxField';
  warnings: {
    IconComponent: React.ComponentType<ComponentProps<SvgIconComponent>>;
    tooltip: string;
    isActive: boolean;
  }[];
  minWarningsIndent: number;
  justifyIcons?: JustifyIconsVariants | ((params: { warningsCount: number }) => JustifyIconsVariants);
};

export const ContextWarnings = memo<ContextWarningsProps>(props => {
  const { warnings, minWarningsIndent = 0, variant, justifyIcons = 'end' } = props;

  const filteredWarnings = warnings.filter(w => w.isActive);
  const indent = Math.max(minWarningsIndent, filteredWarnings.length);

  const classes = useContextWarningsStyles({
    indent,
  });

  const justifyVariant =
    typeof justifyIcons === 'function' ? justifyIcons({ warningsCount: filteredWarnings.length }) : justifyIcons;

  return (
    <div
      className={cx(classes.root, {
        [classes.textFieldWarnings]: variant === 'textField',
        [classes.checkboxWarnings]: variant === 'checkboxField',
        [classes.justifyCenter]: justifyVariant === 'center',
        [classes.justifyStart]: justifyVariant === 'start',
        [classes.justifyEnd]: justifyVariant === 'end',
      })}
    >
      {filteredWarnings.map(({ tooltip, IconComponent }, i) => (
        <Tooltip title={tooltip} key={i}>
          <span className={classes.warn}>
            <IconComponent className={classes.warnIcon} />
          </span>
        </Tooltip>
      ))}
    </div>
  );
});
